import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

export default function ViewPoint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const apiBase = process.env.REACT_APP_SERVER_API_URL || "";
    const base = apiBase.replace(/\/$/, "");

    const { isAuthenticated, isLoading } = useAuth0();

    const [point, setPoint] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // enum display maps: { enumName: { value -> display } }
    const [enumMaps, setEnumMaps] = useState({});

    useEffect(() => {
        let cancelled = false;

        async function fetchPoint() {
            setLoading(true);
            try {
                // Try single-point endpoint first
                let res = await fetch(`${base}/api/points/${encodeURIComponent(id)}`);
                let data = null;
                if (res.ok) {
                    data = await res.json().catch(() => null);
                } else {
                    // fallback: fetch all and find
                    res = await fetch(`${base}/api/points`);
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const all = await res.json();
                    data = Array.isArray(all) ? all.find(p => String(p.id) === String(id)) : null;
                }

                if (cancelled) return;
                if (!data) {
                    setError("Point not found.");
                } else {
                    setPoint(data);
                }
            } catch (err) {
                console.error("Failed to load point:", err);
                if (!cancelled) setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        async function fetchEnums() {
            const enumNames = ["CategoryTypeEnum", "ServicesEnum", "FundingTypeEnum"];
            try {
                const results = await Promise.all(enumNames.map(n => fetch(`${base}/api/enums/${n}`).then(r => r.ok ? r.json().catch(() => []) : [])));
                if (cancelled) return;
                const maps = {};
                for (let i = 0; i < enumNames.length; i++) {
                    const arr = Array.isArray(results[i]) ? results[i] : [];
                    maps[enumNames[i]] = arr.reduce((m, it) => {
                        if (it && typeof it.value !== "undefined") m[String(it.value)] = it.display ?? String(it.value);
                        return m;
                    }, {});
                }
                setEnumMaps(maps);
            } catch (e) {
                console.warn("Failed to load enums:", e);
            }
        }

        fetchPoint();
        fetchEnums();

        return () => { cancelled = true; };
    }, [base, id]);

    if (loading) return <div className="container py-4">Loading�</div>;
    if (error) return <div className="container py-4"><div className="alert alert-danger">{error}</div></div>;
    if (!point) return <div className="container py-4"><div className="alert alert-warning">Point not found.</div></div>;

    // helper to render a label/value row. Accepts `multiline` to preserve newlines.
    const Row = ({ label, value, multiline = false }) => (
        <div className="row mb-3">
            <div className="col-md-4 text-muted sudHeader"><small>{label}</small></div>
            <div className="col-md-8" style={multiline ? { whiteSpace: "pre-wrap" } : undefined}>{value ?? "-"}</div>
        </div>
    );

    function enumDisplay(enumName, rawValue) {
        if (!rawValue && rawValue !== 0) return "-";
        const m = enumMaps[enumName];
        if (m && typeof m[String(rawValue)] !== "undefined") return m[String(rawValue)];
        // fallback: display value as-is (maybe already friendly) or convert from PascalCase -> words
        const s = String(rawValue);
        // attempt to transform PascalCase/Underscore to human text
        const human = s
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/[_\-]/g, " ")
            .replace(/\s+/g, " ")
            .trim();
        return human || s;
    }

    let socials = point.facebook ?? "" + point.instagram ?? "";

    return (
        <main className="container py-4" style={{ maxWidth: 800 }}>
            <div className="position-relative text-center mb-3">
                <div>
                    <h1 className="mb-0 magnoHeader"><strong>{point.name}</strong></h1>
                </div>

                {isAuthenticated && !isLoading && (
                    <div className="position-absolute" style={{ right: 0, top: "50%", transform: "translateY(-50%)" }}>
                        <button type="button"
                            className="btn btn-secondary ms-2"
                            onClick={() => navigate(`/manage/edit/${encodeURIComponent(point.id)}`, { state: { from: location.pathname } })}>Edit</button>
                    </div>
                )}
            </div>

            <div className="card">
                <div className="card-body">
                    <Row label="Website" value={point.website} />
                    <Row label="Address" value={point.civicAddress} />
                    <Row label="Email" value={point.email} />
                    <Row label="Phone" value={point.phone} />
                    <Row label="Socials" value={socials} />
                    <Row label="Category Type" value={enumDisplay("CategoryTypeEnum", point.categoryType ?? point.CategoryType)} />

                    <hr className="my-4" />
                    <h5 className="mb-3 magnoHeader">Asset Details</h5>
                    <Row label="Membership Required" value={(point.membershipRequired === true) ? "Yes" : (point.membershipRequired === false ? "No" : "-")} />
                    <Row label="Detailed Services" value={enumDisplay("ServicesEnum", point.detailedServices ?? point.DetailedServices)} />
                    <Row label="Funding / Business Model" value={enumDisplay("FundingTypeEnum", point.fundingBusinessModel ?? point.FundingBusinessModel)} />
                    <Row label="Category Type" value={enumDisplay("CategoryTypeEnum", point.categoryType ?? point.CategoryType)} />
                    <Row label="Hours of Operation" value={point.hoursOfOperation} multiline />

                    <hr className="my-4" />
                    <h5 className="mb-3 magnoHeader">Community Context</h5>
                    <Row label="Traditional Territory" value={point.traditionalTerritory} />
                    <Row label="Indigenous Affiliation & Equity Involvement" value={point.indigenousAffiliationAndEquityInvolvement} />
                    <Row label="2SLGBTQIA+ Focused" value={point.sLGBTQIAFocused ?? point.slgbtqiaFocused ?? "-"} />
                    <Row label="Accessibility Accommodations" value={point.accessibilityAccommodations} />

                    {isAuthenticated && !isLoading && (
                        <>
                            <hr className="my-4" />
                            <h5 className="mb-3 magnoHeader">Data Management</h5>
                            <Row label="Datat Source" value={point.dataSource} />
                            <Row label="Last Updated" value={point.lastUpdated} />
                            <Row label="Verified" value={point.verified ? "Yes" : "No"} />
                            <Row label="Notes" value={point.notes} />
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}